import store from '@/store/store';
import { Nullable } from '@/utils/types';

const API_URL = process.env.VUE_APP_SESSION_RECORDER_API_URL;
const PROJECT_ID = process.env.VUE_APP_SESSION_RECORDER_PROJECT_ID;
const INGEST_KEY = process.env.VUE_APP_SESSION_RECORDER_INGEST_KEY;

interface IIdentity {
  userId: Nullable<string | number>;
  userName: Nullable<string>;
  userEmail: Nullable<string>;
}

// Who the recorder currently believes it is recording, so a sign-out is told
// apart from an identity that was never set in the first place.
let identifiedUserId: Nullable<string> = null;

function syncIdentity (): void {
  const { userId, userName, userEmail } = store.state as IIdentity;
  const nextUserId = userId ? String(userId) : null;

  if (nextUserId) {
    identifiedUserId = nextUserId;
    window.SessionRecorder?.identify(nextUserId, { name: userName, email: userEmail });
  } else if (identifiedUserId) {
    identifiedUserId = null;
    // Sign-out: end the identified session and start a fresh anonymous one.
    void window.SessionRecorder?.reset();
  }
}

export function initSessionRecorder (): void {
  // No ingest key means every request would be rejected with a 403, so treat a
  // partial configuration the same as recording being switched off.
  if (!API_URL || !PROJECT_ID || !INGEST_KEY) return;

  const script = document.createElement('script');

  script.async = true;
  script.src = `${ API_URL }/recorder.min.js`;
  script.onload = () => {
    window.SessionRecorder?.init({
      apiEndpoint: API_URL,
      projectId: PROJECT_ID,
      ingestKey: INGEST_KEY,
    });

    // Identify immediately, before the recorder has a session. init() only
    // starts initialisation, so this lands while the create request is still
    // being assembled and the identity rides along in its payload. Deferring it
    // until the session exists would leave every row anonymous at creation and
    // make the follow-up PATCH — which has no retry — the only carrier.
    // The persisted store already holds the user on a reload.
    syncIdentity();

    // The whole identity is watched, not just the id: SET_AUTH_INFO and
    // AUTH_USER set userId with no name or email, and SET_CURRENT_USER fills
    // those in afterwards without touching userId. Watching the id alone would
    // pin the session to a bare number and never send the name behind it.
    store.watch(
      (state: IIdentity) => [state.userId, state.userName, state.userEmail].join('|'),
      () => syncIdentity()
    );
  };

  document.head.appendChild(script);
}
